import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { instructorSplitService } from '@/services/instructorSplitService'
import type {
  AssignInstructorInput,
  UpdateSplitInput,
  ValidateSplitsInput,
} from '@/types/instructorSplit'

export function useInstructorSplits(courseId: string) {
  return useQuery({
    queryKey: ['instructor-splits', courseId],
    queryFn: async () => {
      const response = await instructorSplitService.listByCourse(courseId)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch instructor splits')
      }
      return response.data
    },
    enabled: !!courseId,
  })
}

export function useInstructorCourses(userId: string) {
  return useQuery({
    queryKey: ['instructor-courses', userId],
    queryFn: async () => {
      const response = await instructorSplitService.getCoursesByInstructor(userId)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch instructor courses')
      }
      // Return data or empty array to avoid undefined
      return response.data ?? []
    },
    enabled: !!userId,
  })
}

export function useAssignInstructor(courseId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: AssignInstructorInput) =>
      instructorSplitService.assignInstructor(courseId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instructor-splits', courseId] })
      queryClient.invalidateQueries({ queryKey: ['courses', courseId] })
    },
  })
}

export function useUpdateInstructorSplit(courseId: string, userId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateSplitInput) =>
      instructorSplitService.updateSplit(courseId, userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instructor-splits', courseId] })
      queryClient.invalidateQueries({ queryKey: ['instructor-courses', userId] })
    },
  })
}

export function useRemoveInstructor(courseId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: string) => instructorSplitService.removeInstructor(courseId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instructor-splits', courseId] })
    },
  })
}

export function useValidateInstructorSplits(courseId: string) {
  return useMutation({
    mutationFn: (data: ValidateSplitsInput) =>
      instructorSplitService.validateSplits(courseId, data),
  })
}

export function useInstructorAssignedModules(userId: string, courseId: string) {
  return useQuery({
    queryKey: ['instructor-assigned-modules', userId, courseId],
    queryFn: async () => {
      const response = await instructorSplitService.getAssignedModules(userId, courseId)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch assigned modules')
      }
      return response.data
    },
    enabled: !!userId && !!courseId,
  })
}
